import React, { useEffect, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

import { useUtilityStates } from "../useStates";
import Button from "../../../components/atomic/Button";
import { notify } from "../../../shared/context/AlertContext";
import GetAuthContext from "../../../shared/context/AuthContext";
import { LoanApplicationService } from "../../../services/LoanApplication";
import { formatDate } from "../OfferCalculator/OfferCalculatorFunctions";
import { formatNumberIntl } from "../../../shared/utils/DateUtil";

const DETAILS = [
  { key: "processingFee", label: "Processing Fee(%)" },
  { key: "createdAt", label: "Created at" },
  { key: "lenderName", label: "Lender" },
];
const order = ["firstName", "lastName", "gender", "dob", "email"];
const dateKeys: Set<string> = new Set([
  "Expiry At",
  "dateOfIncorporation",
  "dob",
]);
const numberKeys: Set<string> = new Set(["maxAmount", "maxEMI", "minAmount"]);
const monthKeys: Set<string> = new Set(["maxTenure", "minTenure"]);

const labelHandler = (name: string) => {
  const spacedString = name?.replace(/([a-z])([A-Z])/g, "$1 $2");

  return spacedString?.charAt(0).toUpperCase() + spacedString.slice(1);
};

export default function Card({ offer, pageName, index }: any) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const { fetchToken } = GetAuthContext();
  const { setSelectedOffer, setSidebar } = useUtilityStates((state) => state);
  const [isExpand, setIsExpand] = React.useState<boolean>(false);
  const [isEDIProgramme, setisEDIProgramee] = useState(false);
  const [additionalParameters, setAdditionalParameters] = useState<string[]>(
    []
  );

  useEffect(() => {
    const additionalParameters = offer?.additionalParameters ?? {};
    const allKeys = Object.keys(additionalParameters);

    // Separate the keys into ordered and unordered
    const orderedKeys = order.filter((key) => allKeys.includes(key));
    const unorderedKeys = allKeys.filter((key) => !order.includes(key));

    // Combine ordered and unordered keys
    const sortedKeys = [...orderedKeys, ...unorderedKeys];
    setAdditionalParameters(sortedKeys);
    setisEDIProgramee(
      additionalParameters?.installmentProgramme == "EDI" ? true : false
    );
  }, [offer?.additionalParameters]);

  const sendLinkHandler = async () => {
    setLoading(true);

    try {
      const payload = {
        token: fetchToken(),
        pageName: pageName,
        sourceEntityID: offer?.sourceEntityID,
        payload: {
          mobile: offer?.mobile,
          sourceEntityID: offer?.sourceEntityID,
          partnerCode: offer?.partner_code,
        },
      };

      const response = (await LoanApplicationService.addUser(payload)) as any;
      if (response?.status) {
        notify({
          title: "Success",
          text: response?.data?.msg,
          type: "success",
        });
        const payload2 = {
          token: fetchToken(),
          pageName: pageName,
          sourceEntityID: offer?.sourceEntityID,
          payload: {
            userID: response?.data?.userID,
          },
        };
        const res =
          await LoanApplicationService?.sendSessionLinkToCustomer(payload2);
        if (res?.status) {
          setSelectedOffer(offer);
          setSidebar({ isOpen: true, current: "success" });
          notify({
            title: "Success",
            type: "success",
            text: "Send Session Link to Customer",
          });
        } else {
          notify({
            title: "Error",
            text: response.error,
          });
        }
      } else {
        notify({
          title: "Error",
          text: response.error,
        });
      }
    } catch (e) {
      notify({
        title: "Error",
        text: "An unexpected error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOfferCalculatorClick = () => {
    setSidebar({
      isOpen: true,
      current: "offer-calculator",
      offerIndex: index,
    });
  };

  return (
    <div className="rounded-xl border p-4">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <div className="text-[#101828] font-medium text-xl">
            {offer?.sourceEntityName}
          </div>
          <div className="text-[#475467] text-sm font-normal mt-2.5">
            {offer?.lenderName}
          </div>
        </div>

        <div className="col-span-8 grid">
          <div className="grid grid-cols-1 gap-1">
            <div className="text-center">
              <div className="text-[#343F59] font-bold text-2xl">
                {formatNumberIntl(offer?.amount || 0)}
              </div>
              <div className="text-[#626262] text-xs font-normal mt-2.5">
                Loan Amount
              </div>
            </div>
           
          </div>
          <div className="grid grid-cols-2 gap-1 border-t p-2">
          <div className="text-center border-r">
              <div className="text-[#343F59] font-bold text-small">
                {offer?.interest}%
              </div>
              <div className="text-[#626262] text-xs font-normal mt-2.5">
                Interest rate
              </div>
            </div>
            <div className="text-center">
              <div className="text-[#343F59] font-bold text-small">
                {offer?.tenure}
              </div>
              <div className="text-[#626262] text-xs font-normal mt-2.5">
                Tenure {isEDIProgramme ? "(Days)" : "(months)"}
              </div>
            </div>
        </div>
        </div>

        <div className="col-span-12 flex items-center  gap-2 justify-end">
          {!isEDIProgramme && (
            <Button
              onClick={handleOfferCalculatorClick}
              outline
              color="primary"
              className="mr-2"
            >
              Emi Calculator
            </Button>
          )}
          <Button
            color="primary"
            disabled={loading}
            onClick={sendLinkHandler}
            className="text-xs font-medium"
          >
            Send Link
          </Button>
          <Button
            outline
            className="ml-3 !p-2"
            onClick={() => setIsExpand((prev) => !prev)}
          >
            {isExpand ? (
              <ChevronUpIcon className="h-4 w-4 text-[#4C4C4D]" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-[#4C4C4D]" />
            )}
          </Button>
        </div>
      </div>

      {isExpand && (
        <>
          <div className="border-t mt-10 pt-10">
            <div className="text-[#373737] text-xl font-medium">
              Loan Details
            </div>
            <div className="mt-5 grid grid-cols-3 gap-1">
              {DETAILS?.map((detail) => (
                <Item
                  key={detail.key}
                  label={detail.label}
                  value={
                    detail.key == "createdAt"
                      ? formatDate(offer?.[detail.key])
                      : offer?.[detail.key]
                  }
                />
              ))}
            </div>
          </div>

          <div className="mt-3 p-3 pl-0 border-t">
            <div className="text-[#373737] text-xl font-medium">
              Additional Parameters
            </div>
            <div className="mt-5 grid grid-cols-2 h-40 overflow-y-auto">
              {additionalParameters.map((key) => {
                // Format date fields if key matches the specified date keys
                const displayValue =
                  dateKeys.has(key) && offer?.additionalParameters?.[key]
                    ? formatDate(offer?.additionalParameters?.[key])
                    : numberKeys.has(key) && offer?.additionalParameters?.[key]
                      ? formatNumberIntl(
                          offer?.additionalParameters?.[key] || 0
                        )
                      : offer?.additionalParameters?.[key];

                return (
                  <div
                    key={key}
                    className="mb-10"
                  >
                    <Item
                      key={key}
                      label={labelHandler(key)}
                      value={
                        monthKeys.has(key)
                          ? displayValue +
                            (isEDIProgramme ? " Days" : " Months")
                          : displayValue
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Item = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="text-sm font-medium">
      <div className="text-[#373737]">{label}</div>
      <div className="text-[#626262] mt-1">{value || "-"}</div>
    </div>
  );
};
